import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightIcon, CheckSquareIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserSelect } from '@/components/common/UserSelect';
import { useCan } from '@/hooks/useCan';
import { TASK_PRIORITIES } from './workbookTaskRules';
import { useTaskPriorityLabels } from './taskPriorityLabels';
import type { ProcessStepActionProps } from './workbookIntegrations';

/**
 * Tasks' Process step action: a human-in-the-loop step. Opens a Task for this
 * Instruction, assigned to someone, linked back to the Workbook.
 */
export function CreateStepTask({
  workbookId,
  stepId,
  disabled,
}: ProcessStepActionProps) {
  const gt = useGT();
  const priorities = useTaskPriorityLabels();
  const [canCreate] = useCan(Action.Create, Subject.Task);
  const [open, setOpen] = useState(false);
  const [assigneeUserId, setAssignee] = useState<string | null>(null);
  const [priority, setPriority] =
    useState<(typeof TASK_PRIORITIES)[number]>('medium');
  // One request per Task: a retry resubmits it (the server opens no second
  // Task), and only a Task opened makes the next submit a new request.
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const queryClient = useQueryClient();
  const create = useMutation(
    orpc.workbooks.createStepTask.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.workbooks.tasks.key(),
        }),
    }),
  );
  if (!canCreate) return null;
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) return;
        if (create.data) setRequestId(crypto.randomUUID());
        create.reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" disabled={disabled}>
          <CheckSquareIcon />
          <T context="workbook create a task for a process step">Assign task</T>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex w-80 flex-col gap-2"
        aria-label={gt('Assign task', {
          context: 'workbook create a task for a process step',
        })}
      >
        <p className="text-sm text-muted-foreground">
          <T context="workbook step task help">
            Opens a task with this step&apos;s instructions, linked back to the
            workbook.
          </T>
        </p>
        <label className="grid gap-1 text-sm">
          <T context="workbook step task assignee label">Assignee</T>
          <UserSelect value={assigneeUserId} onChange={setAssignee} />
        </label>
        <label className="grid gap-1 text-sm">
          <T context="workbook step task priority label">Priority</T>
          <Select
            value={priority}
            onValueChange={(value) => {
              const chosen = TASK_PRIORITIES.find((item) => item === value);
              if (chosen) setPriority(chosen);
            }}
          >
            <SelectTrigger
              aria-label={gt('Task priority', {
                context: 'workbook step task priority picker',
              })}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_PRIORITIES.map((item) => (
                <SelectItem key={item} value={item}>
                  {priorities[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        {create.data ? (
          <p role="status" className="text-sm">
            <Link
              to="/tasks/$taskId"
              params={{ taskId: create.data.taskId }}
              className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2"
            >
              {create.data.displayId
                ? gt('Open {id}', {
                    context: 'workbook step task created: link to the task',
                    id: create.data.displayId,
                  })
                : gt('Open the task', {
                    context: 'workbook step task created: link to the task',
                  })}
              <ArrowRightIcon className="size-3.5" />
            </Link>
          </p>
        ) : (
          <Button
            size="sm"
            disabled={create.isPending}
            onClick={() =>
              create.mutate({
                workbookId,
                stepId,
                assigneeUserId,
                priority,
                requestId,
              })
            }
          >
            <T context="workbook create step task">Create task</T>
          </Button>
        )}
        {create.error && (
          <p role="alert" className="text-sm text-destructive">
            {create.error.message}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
